import React, { createContext, useContext, useState, useEffect } from "react";
import { Patient, getPatientByEmail, createPatient } from "@/services/data";

interface AuthContextType {
  patient: Patient | null;
  isLoggedIn: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  adminLogin: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateProfile: (updates: Partial<Patient>) => void;
}

interface RegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
  dob: string;
  gender: "Male" | "Female" | "Other";
  address: string;
  city: string;
  pincode: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Simulated credentials
const ADMIN_CREDENTIALS = { email: "admin@diagnoplus.com", password: "admin123" };
const DEMO_PATIENT_PASSWORD = "patient123";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Restore session from localStorage
    const savedPatient = localStorage.getItem("diagno_patient");
    const savedAdmin = localStorage.getItem("diagno_admin");
    if (savedAdmin === "true") setIsAdmin(true);
    if (savedPatient) {
      try {
        setPatient(JSON.parse(savedPatient));
      } catch (e) {
        console.log("Failed to restore patient session");
      }
    }
  }, []);

  const login = async (email: string, password: string) => {
    const found = await getPatientByEmail(email);
    if (!found) return { success: false, error: "No account found with this email." };
    // For demo, accept the demo password OR the first 4 chars of phone
    if (password !== DEMO_PATIENT_PASSWORD && password !== found.phone.slice(0, 4)) {
      return { success: false, error: "Incorrect password. (Hint: use 'patient123')" };
    }
    setPatient(found);
    localStorage.setItem("diagno_patient", JSON.stringify(found));
    console.log("Patient logged in:", found.name);
    return { success: true };
  };

  const adminLogin = async (email: string, password: string) => {
    if (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
      setIsAdmin(true);
      localStorage.setItem("diagno_admin", "true");
      return { success: true };
    }
    return { success: false, error: "Invalid admin credentials." };
  };

  const register = async (data: RegisterData) => {
    const existing = await getPatientByEmail(data.email);
    if (existing) return { success: false, error: "An account with this email already exists." };
    const newPatient = await createPatient({
      name: data.name,
      email: data.email,
      phone: data.phone,
      dob: data.dob,
      gender: data.gender,
      address: data.address,
      city: data.city,
      pincode: data.pincode,
    });
    setPatient(newPatient);
    localStorage.setItem("diagno_patient", JSON.stringify(newPatient));
    console.log("New patient registered:", newPatient.name);
    return { success: true };
  };

  const logout = () => {
    setPatient(null);
    setIsAdmin(false);
    localStorage.removeItem("diagno_patient");
    localStorage.removeItem("diagno_admin");
    console.log("Logged out");
  };

  const updateProfile = (updates: Partial<Patient>) => {
    if (!patient) return;
    const updated = { ...patient, ...updates };
    setPatient(updated);
    localStorage.setItem("diagno_patient", JSON.stringify(updated));
  };

  return (
    <AuthContext.Provider value={{
      patient,
      isLoggedIn: !!patient || isAdmin,
      isAdmin,
      login,
      adminLogin,
      register,
      logout,
      updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
